/**
 * A barra de título de todo painel da interface, e a memória de quem está recolhido.
 *
 * ## Por que uma peça só para isso
 *
 * O inventário já recolhia — clique no título, `display: none` em tudo que não fosse o
 * título — e era o **único**. O resto da interface tinha três comportamentos diferentes para
 * a mesma pergunta "some da minha frente": o minimapa e o bestiário abrem e fecham por tecla
 * e não têm meio-termo, a loja fecha mandando pacote ao servidor, e o painel de status não
 * fechava de jeito nenhum. Quem recolhia o inventário aprendia um gesto que não valia em
 * nenhum outro lugar da tela.
 *
 * Recolher é diferente de fechar, e a diferença é o que justifica o gesto existir em todos
 * eles. Fechar é dizer "não quero mais isto"; recolher é dizer "quero isto, mas agora preciso
 * do espaço". Numa coluna que reparte 604 px de piso entre quatro painéis (ver `#sidebar` em
 * `style.css`), o espaço é a moeda escassa — e quem está comprando na loja não precisa das
 * sete perícias na tela ao mesmo tempo.
 *
 * ## Por que o estado sobrevive ao recarregar
 *
 * A escolha de recolher é uma preferência de layout, não um estado de jogo: quem joga de
 * notebook recolhe o diagnóstico e as perícias uma vez e quer encontrá-los recolhidos amanhã.
 * Sem memória, cada recarregamento devolveria a tela que a pessoa acabou de arrumar — e o
 * cliente recarrega em toda troca de região que derruba a conexão.
 *
 * Fica em `localStorage` e **não** no servidor, apesar de o servidor já guardar personagem e
 * inventário. Isto é preferência de **máquina**, não de conta: a mesma pessoa quer o chat
 * grande no monitor de 27" e recolhido no notebook, e um campo por painel no banco resolveria
 * o problema errado ao custo de uma migração. O preço é que a escolha não viaja entre
 * computadores, que é exatamente o comportamento desejado aqui.
 */

/**
 * Quem tem barra de título. É união fechada de propósito: a chave vai para o disco e
 * sobrevive a mudanças de código, então um `string` solto viraria lixo acumulado em
 * `localStorage` no dia em que um painel fosse renomeado.
 */
export type PanelId =
  | 'status'
  | 'equipamento'
  | 'inventario'
  | 'minimapa'
  | 'social'
  | 'bestiario'
  // As automações: o painel inteiro, e cada uma das seções dentro dele. Uma chave por seção e
  // não uma só, porque recolher é por seção — quem só usa o auto-loot deixa as outras
  // fechadas, e é essa a economia de espaço que a barra de título existe para dar.
  | 'automacoes'
  | 'autoloot'
  | 'autocomida'
  | 'autocura'
  | 'autocacada'
  | 'magias'
  | 'chat'
  | 'loja'
  | 'banco'
  | 'troca';

const STORAGE_KEY = 'hexaria:paineis';

export interface PanelHeader {
  /** A própria barra, já pronta para entrar no painel. */
  readonly root: HTMLElement;
  /** Troca o texto sem reconstruir a barra — o inventário reescreve o peso a cada item. */
  setLabel(text: string): void;
  toggle(): void;
  readonly collapsed: boolean;
}

export interface PanelOptions {
  /**
   * Botões que ficam à direita, na mesma linha do título — o "fechar" da loja, por exemplo.
   * O clique neles **não** recolhe o painel: ver `stopPropagation` abaixo.
   */
  actions?: readonly HTMLElement[];
  /**
   * Avisa o painel de que ele acabou de recolher ou expandir.
   *
   * Existe para quem **desenha** e não só empilha texto: o minimapa pinta 4.225 tiles a cada
   * passo e pula o trabalho enquanto está recolhido, então ele precisa saber a hora de
   * invalidar o que ficou para trás. Sem este aviso, quem expandisse o mapa veria o desenho
   * de onde estava antes até dar o próximo passo.
   *
   * Não dispara na criação: aplicar o estado guardado não é o jogador mexendo em nada, e um
   * aviso ali obrigaria todo painel a aguentar ser avisado antes de terminar de nascer.
   */
  onToggle?: (collapsed: boolean) => void;
}

/**
 * Prepara `panel` para recolher e devolve a barra de título a ser inserida nele.
 *
 * O chamador é quem insere, e sempre como **primeiro** filho: a regra de CSS que esconde o
 * conteúdo recolhido é `> *:not(.panel-title)`, então a barra precisa ser filha direta do
 * mesmo elemento que ganha a classe `collapsed`.
 *
 * O estado guardado é aplicado aqui, na criação. É o que faz os painéis que se redesenham
 * inteiros — inventário, amigos e loja fazem `innerHTML = ''` a cada pacote — não perderem o
 * recolhimento no meio de uma sessão: a barra nova nasce sabendo o que a antiga sabia.
 */
export function collapsiblePanel(
  panel: HTMLElement,
  id: PanelId,
  label: string,
  options: PanelOptions = {},
): PanelHeader {
  panel.classList.add('panel');

  const root = document.createElement('div');
  root.className = 'panel-title';

  const chevron = document.createElement('span');
  chevron.className = 'panel-chevron';
  chevron.setAttribute('aria-hidden', 'true');

  const caption = document.createElement('span');
  caption.className = 'panel-label';
  caption.textContent = label;

  root.append(chevron, caption);

  if (options.actions?.length) {
    const actions = document.createElement('span');
    actions.className = 'panel-actions';
    actions.append(...options.actions);
    // Sem isto, fechar a loja recolheria o painel no caminho — e o estado recolhido ficaria
    // salvo para a próxima loja que abrisse, que é um painel vazio que ninguém pediu.
    actions.addEventListener('click', (event) => event.stopPropagation());
    root.append(actions);
  }

  let collapsed = readState()[id] === true;

  const apply = (): void => {
    panel.classList.toggle('collapsed', collapsed);
    chevron.textContent = collapsed ? '▸' : '▾';
    root.setAttribute('aria-expanded', String(!collapsed));
    root.title = collapsed ? 'expandir' : 'recolher';
  };

  const toggle = (): void => {
    collapsed = !collapsed;
    apply();
    writeState(id, collapsed);
    options.onToggle?.(collapsed);
  };

  apply();
  root.addEventListener('click', toggle);

  return {
    root,
    setLabel: (text: string) => {
      caption.textContent = text;
    },
    toggle,
    get collapsed() {
      return collapsed;
    },
  };
}

// --- Memória ----------------------------------------------------------------

/**
 * Um registro só para todos os painéis, e não uma chave por painel.
 *
 * São dez painéis: dez chaves seriam dez leituras de `localStorage` em cada montagem da
 * interface, e `localStorage` é síncrono — cada leitura para a thread que está desenhando.
 * Um objeto pequeno custa uma leitura e um `JSON.parse`.
 */
type PanelState = Partial<Record<PanelId, boolean>>;

/**
 * Tudo passa por `try`, e não é paranoia de bibliotecário.
 *
 * `localStorage` **lança** em três situações que este jogo encontra de verdade: navegação
 * privada do Safari, cota estourada, e a página aberta num `iframe` de terceiro com cookies
 * bloqueados. Uma exceção aqui subiria pelo construtor do painel e derrubaria a montagem da
 * interface inteira — o jogo não abriria porque não conseguiu lembrar se o chat estava
 * recolhido. Falhar em silêncio degrada para "nasce tudo expandido", que é o padrão.
 */
function readState(): PanelState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as PanelState) : {};
  } catch {
    return {};
  }
}

function writeState(id: PanelId, collapsed: boolean): void {
  try {
    const state = readState();

    // Recolhido vira campo; expandido **apaga** o campo. O padrão do jogo é expandido, então
    // gravar `false` seria gravar o padrão — e o registro cresceria com dez linhas que não
    // dizem nada. Assim ele só guarda o que difere.
    if (collapsed) state[id] = true;
    else delete state[id];

    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ver `readState`: perder a preferência é aceitável, derrubar a interface não é.
  }
}
